import { ExternalCalendarEvent, IDPrefixes } from '@griply/types';
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  format,
  parse,
  startOfDay,
} from 'date-fns';

import { createCompositeId } from './encoded-ids/composite-id';

export const createDailyEventsFromExternalEvents = (
  externalEvents: ExternalCalendarEvent[],
) =>
  externalEvents.reduce<ExternalCalendarEvent[]>((acc, externalEvent) => {
    const isAllDay = externalEvent.start.date && externalEvent.end.date;
    const startDate = externalEvent.start.dateTime
      ? new Date(externalEvent.start.dateTime)
      : parse(externalEvent.start.date!, 'yyyy-MM-dd', new Date());
    const endDate = externalEvent.end.dateTime
      ? new Date(externalEvent.end.dateTime)
      : parse(externalEvent.end.date!, 'yyyy-MM-dd', new Date());

    const amountOfDays = isAllDay
      ? // when the `date` is set, it means that the event is all day.
        // in this case, it is about the amount of days between the start and end date, as the endDate is not taken into account.
        differenceInDays(externalEvent.end.date!, externalEvent.start.date!)
      : eachDayOfInterval({
          start: startDate,
          end: endDate,
        }).length;

    if (amountOfDays <= 1) {
      acc.push(externalEvent);
      return acc;
    }

    Array.from({ length: amountOfDays }, (_, dayOffset) => {
      const currentDay = addDays(startDate, dayOffset);

      if (isAllDay) {
        const dateString = format(currentDay, 'yyyy-MM-dd');
        acc.push({
          ...externalEvent,
          id: createCompositeId(IDPrefixes.MultiDayEvent, [
            externalEvent.id,
            String(dayOffset),
          ]),
          start: { date: dateString },
          end: { date: dateString },
        });
        return;
      }

      // Timed event logic
      const isFirstDay = dayOffset === 0;
      const isLastDay = dayOffset === amountOfDays - 1;

      const startDateTime = isFirstDay
        ? externalEvent.start.dateTime!
        : format(startOfDay(currentDay), "yyyy-MM-dd'T'HH:mm:ss");

      const endDateTime = isLastDay
        ? externalEvent.end.dateTime!
        : format(endOfDay(currentDay), "yyyy-MM-dd'T'HH:mm:ss");

      acc.push({
        ...externalEvent,
        id: createCompositeId(IDPrefixes.MultiDayEvent, [
          externalEvent.id,
          String(dayOffset),
        ]),
        start: { dateTime: startDateTime },
        end: { dateTime: endDateTime },
      });
    });

    return acc;
  }, []);
